import type { ToolCallExecutionDetails } from "@repo/app-types/api/interviewLivekitAgent";
import { IconChevronDown } from "@tabler/icons-react";
import type { FC, ReactNode } from "react";
import {
  Button as AriaButton,
  Disclosure,
  DisclosurePanel,
  Heading,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { PH_MASK_CLASS } from "../../analytics/sessionRecordingMasking.js";
import { CodeToolRunDetails } from "./codeToolRunDetails.js";

interface ToolCallExecutionDetailsViewProps {
  executionDetails: ToolCallExecutionDetails;
}

/**
 * What a custom or code tool call did: the HTTP exchange, or the script's
 * requests and logs, and how long it took. It is all collapsed behind one
 * disclosure, since a caller's own Output/Result/Error rows already say how
 * the call turned out.
 */
export const ToolCallExecutionDetailsView: FC<
  ToolCallExecutionDetailsViewProps
> = (props) => {
  const { executionDetails } = props;
  const turnaroundTime = `${(executionDetails.duration_ms / 1000).toFixed(2)} s`;

  if (executionDetails.transport === "code") {
    return (
      <ToolCallDetailsDisclosure label="Request details">
        {executionDetails.error ? (
          <ToolCallDetail
            label="Error"
            value={`${executionDetails.error.code}: ${executionDetails.error.message}`}
          />
        ) : null}
        <CodeToolRunDetails
          requests={executionDetails.requests}
          logs={executionDetails.logs}
        />
        <ToolCallDetail label="Turnaround time" value={turnaroundTime} />
      </ToolCallDetailsDisclosure>
    );
  }

  const isMocked = executionDetails.mode === "mocked";
  // The body exactly as the endpoint sent it, or as the mock defines it.
  const responseBody =
    !isMocked && !executionDetails.response.received
      ? "(no response received)"
      : executionDetails.response.body || "(empty)";
  return (
    <ToolCallDetailsDisclosure label="Request details">
      <ToolCallDetail
        label="Request URL"
        value={executionDetails.request.url}
      />
      <ToolCallDetail
        label="Headers"
        value={JSON.stringify(executionDetails.request.headers, null, 2)}
      />
      <ToolCallDetail
        label="Request body"
        value={executionDetails.request.body ?? "(none)"}
      />
      <ToolCallDetail
        label="Response status"
        value={
          isMocked
            ? "Mocked (no HTTP request was made)"
            : (executionDetails.response.status?.toString() ?? "No response")
        }
      />
      <ToolCallDetail
        label={isMocked ? "Mocked response body" : "Response body"}
        value={responseBody}
      />
      <ToolCallDetail label="Turnaround time" value={turnaroundTime} />
    </ToolCallDetailsDisclosure>
  );
};

interface ToolCallDetailsDisclosureProps {
  label: string;
  children: ReactNode;
}

/** Details that are there when needed but collapsed by default. */
const ToolCallDetailsDisclosure: FC<ToolCallDetailsDisclosureProps> = (
  props,
) => {
  const { label, children } = props;
  return (
    <Disclosure>
      <Heading>
        <AriaButton
          slot="trigger"
          className="group flex cursor-pointer items-center gap-1 font-medium text-muted-foreground"
        >
          <IconChevronDown className="size-4 shrink-0 transition-transform group-aria-expanded:rotate-180" />
          {label}
        </AriaButton>
      </Heading>
      <DisclosurePanel>
        <div className="space-y-3 pt-2">{children}</div>
      </DisclosurePanel>
    </Disclosure>
  );
};

interface ToolCallDetailProps {
  label: string;
  value: string;
}

export const ToolCallDetail: FC<ToolCallDetailProps> = (props) => {
  const { label, value } = props;
  return (
    <div>
      <div className="mb-1 font-medium text-muted-foreground">{label}</div>
      <pre
        className={twMerge(
          "overflow-x-auto rounded bg-gray-50 p-2 font-mono text-[11px] break-all whitespace-pre-wrap",
          PH_MASK_CLASS,
        )}
      >
        {value}
      </pre>
    </div>
  );
};
